import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { Pressable, Text, View } from "react-native";
import moment from "moment";
import { faAngleLeft, faAngleRight } from "@fortawesome/pro-light-svg-icons";
import { styledRN } from "./styled";
import { HiylloBG } from "./hiyllo-bg-provider";
import { useFontAwesomeIcon } from "./platform";
import { useTheme } from "./theme";
const DayCircleElement = styledRN(View, ({ $theme, size, active }) => ({
    height: size,
    width: size,
    fontSize: 12,
    backgroundColor: active ? 'transparent' : $theme.background2,
    color: $theme.foreground,
    borderRadius: size / 2,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
}));
const DayCirclePlaceholder = styledRN(View, ({ $theme, size }) => ({
    height: size,
    width: size,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: $theme.midground,
    fontSize: 12,
}));
const MonthHeaderRow = styledRN(View, ({ $theme }) => ({
    height: 24,
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    color: $theme.foregroundInactive,
    fontSize: 16,
    userSelect: 'none',
    flexDirection: 'row',
}));
const MonthChangeIcon = styledRN(View, ({ $theme }) => ({
    height: 24,
    width: 24,
    backgroundColor: $theme.background3,
    borderRadius: 12,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
}));
const MonthGrid = styledRN(View, ({ fullWidth }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    userSelect: 'none',
    width: fullWidth ? '100%' : 230,
}));
const WeekRow = styledRN(View, ({ $theme, active, size }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    curor: 'pointer',
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 5,
    paddingRight: 5,
    backgroundColor: active ? $theme.background1 : 'transparent',
    borderRadius: 10,
    gap: size * 0.3,
}));
const DayCirclePlaceholderText = styledRN(Text, () => ({
    color: '#37363a',
    fontFamily: 'hiyllo',
    fontSize: 12,
}));
const DayCircleSizeCtx = React.createContext(24);
const DayCircle = React.memo(function DayCircle(props) {
    const onClick = React.useCallback(() => {
        props.onChangeDate(moment(props.weekOf).date(props.day).toDate());
    }, [props]);
    const size = React.useContext(DayCircleSizeCtx);
    const isToday = moment().format('MMDDYY') ===
        moment(props.weekOf).date(props.day).format('MMDDYY');
    const active = props.useTodayAsSelected
        ? isToday
        : moment(props.weekOf).format('MMDDYY') ===
            moment(props.weekOf).date(props.day).format('MMDDYY');
    const OutlineElement = isToday ? HiylloBG : View;
    const theme = useTheme();
    return (_jsx(Pressable, { onPress: onClick, children: _jsx(OutlineElement, { style: {
                backgroundColor: theme.midground,
                height: size,
                width: size,
                borderRadius: size / 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }, children: _jsx(DayCircleElement, { size: size - 2.5, active: active, children: _jsx(Text, { style: {
                        color: 'white',
                        fontFamily: 'hiyllo',
                        fontSize: size * 0.4,
                    }, children: props.day }) }) }) }));
});
var WeeksStartOn;
(function (WeeksStartOn) {
    WeeksStartOn["sunday"] = "SUN";
    WeeksStartOn["monday"] = "MON";
})(WeeksStartOn || (WeeksStartOn = {}));
export const MiniMonthCalendar = React.memo(function MiniMonthCalendar(props) {
    const FontAwesomeIcon = useFontAwesomeIcon();
    const month = React.useMemo(() => moment(props.weekOf).format('MMMYY'), [props.weekOf]);
    const daysInMonth = React.useMemo(() => moment(props.weekOf).daysInMonth(), [props.weekOf]);
    const weeksStartOn = WeeksStartOn.monday;
    const weeks = React.useMemo(() => {
        const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
        const firstDayOfMonth = moment(props.weekOf).startOf('month');
        const weeks = [];
        let currentWeek = [];
        for (let i = 1; i <
            (weeksStartOn === WeeksStartOn.sunday
                ? firstDayOfMonth.day() + 6
                : firstDayOfMonth.day() === 0 ? 7 : firstDayOfMonth.day()); i++) {
            currentWeek.push(null);
        }
        for (let i = 0; i < daysInMonth; i++) {
            currentWeek.push(days[i]);
            if (currentWeek.length === 7) {
                weeks.push(currentWeek);
                currentWeek = [];
            }
        }
        if (currentWeek.length > 0) {
            for (let i = currentWeek.length; i < 7; i++) {
                currentWeek.push(null);
            }
            weeks.push(currentWeek);
        }
        return weeks;
    }, [daysInMonth, props.weekOf, weeksStartOn]);
    const onChangeDate = React.useCallback((date) => {
        props.onChangeDate(date);
    }, [props]);
    const { onChangeMonth } = props;
    const onPreviousMonth = React.useCallback(() => {
        const previousMonth = moment(props.weekOf).subtract(1, 'month').toDate();
        if (onChangeMonth != null) {
            onChangeMonth(previousMonth);
        }
        else {
            onChangeDate(previousMonth);
        }
    }, [onChangeDate, props.weekOf, onChangeMonth]);
    const onNextMonth = React.useCallback(() => {
        const nextMonth = moment(props.weekOf).add(1, 'month').toDate();
        if (onChangeMonth != null) {
            onChangeMonth(nextMonth);
        }
        else {
            onChangeDate(nextMonth);
        }
    }, [onChangeDate, props.weekOf, onChangeMonth]);
    const isWeekOfProp = (weekRow) => {
        return weekRow.includes(moment(props.weekOf).date());
    };
    const size = props.size ?? 24;
    return (_jsx(DayCircleSizeCtx.Provider, { value: size, children: _jsxs(MonthGrid, { fullWidth: props.fullWidth, children: [_jsxs(MonthHeaderRow, { children: [_jsx(Pressable, { onPress: onPreviousMonth, children: _jsx(MonthChangeIcon, { children: _jsx(FontAwesomeIcon, { icon: faAngleLeft, color: "#656570" }) }) }), _jsx(View, { style: { width: '50%' }, children: _jsx(Text, { style: {
                                    textAlign: 'center',
                                    fontFamily: 'hiyllo',
                                    color: '#656570',
                                    fontSize: 16,
                                }, children: moment(props.weekOf).format(moment(props.weekOf).year() === moment().year()
                                    ? 'MMMM'
                                    : 'MMMM, YYYY') }) }), _jsx(Pressable, { onPress: onNextMonth, children: _jsx(MonthChangeIcon, { children: _jsx(FontAwesomeIcon, { icon: faAngleRight, color: "#656570" }) }) })] }), _jsxs(WeekRow, { active: false, size: size, children: [_jsx(DayCirclePlaceholder, { size: size, children: _jsx(DayCirclePlaceholderText, { children: "M" }) }), _jsx(DayCirclePlaceholder, { size: size, children: _jsx(DayCirclePlaceholderText, { children: "T" }) }), _jsx(DayCirclePlaceholder, { size: size, children: _jsx(DayCirclePlaceholderText, { children: "W" }) }), _jsx(DayCirclePlaceholder, { size: size, children: _jsx(DayCirclePlaceholderText, { children: "T" }) }), _jsx(DayCirclePlaceholder, { size: size, children: _jsx(DayCirclePlaceholderText, { children: "F" }) }), _jsx(DayCirclePlaceholder, { size: size, children: _jsx(DayCirclePlaceholderText, { children: "S" }) }), _jsx(DayCirclePlaceholder, { size: size, children: _jsx(DayCirclePlaceholderText, { children: "S" }) })] }), weeks.map((week, weekIndex) => (_jsx(WeekRow, { active: isWeekOfProp(week), size: size, children: week.map((day, dayIndex) => day === null ? (_jsx(DayCirclePlaceholder, { size: size }, month + dayIndex.toString())) : (_jsx(DayCircle, { day: day, weekOf: props.weekOf, onChangeDate: onChangeDate, useTodayAsSelected: !props.useWeekOfAsActive }, month + day.toString()))) }, month + weekIndex.toString())))] }) }));
});
